import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { InstagramAccount } from "../api/types";

/**
 * Persistent banner that surfaces accounts that need user intervention from
 * ANY page in the app. Without this, an account that quietly transitions to
 * `awaiting_2fa_code` server-side would only become visible if the user
 * happens to be on `/instagram-accounts` and reloads — meaning a worker job
 * stays parked indefinitely.
 *
 * Cases (priority highest → lowest):
 *   1. At least one account is `awaiting_2fa_code` → red CTA to enter code.
 *   2. At least one account is in `error` with `lastFailureKind=auth_required`
 *      (most common after a soft logout) → amber CTA to reopen the verify
 *      flow, which will park at 2FA and surface the input automatically.
 *   3. Anything else → no banner.
 *
 * The banner self-hides on the `/instagram-accounts` page (where the user
 * already has the per-account UI in front of them) to avoid duplication.
 */
export default function IgAccountAttentionBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const query = useQuery({
    queryKey: ["instagram-accounts", "attention-banner"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
    // Poll every 5s. We don't want to be stale by more than that since we're
    // signalling that USER ACTION is required, not just status. The query
    // shares its cache key with the `/instagram-accounts` page (`["instagram-accounts"]`)
    // separately, so we use a distinct key here to avoid colliding refetch
    // intervals that would compete with the per-page polling.
    refetchInterval: 5_000,
    retry: false,
    staleTime: 0,
  });

  // Don't double-show on /instagram-accounts itself.
  if (location.pathname.startsWith("/instagram-accounts")) return null;
  const accounts = query.data || [];
  if (accounts.length === 0) return null;

  const awaiting2fa = accounts.filter((a) => a.status === "awaiting_2fa_code");
  const auth_required_in_error = accounts.filter(
    (a) =>
      (a.status === "error" || a.status === "challenge_required") &&
      a.lastFailureKind === "auth_required"
  );

  if (awaiting2fa.length > 0) {
    const usernames = awaiting2fa.map((a) => `@${a.igUsername}`).join(", ");
    return (
      <Banner tone="rose">
        <span>
          {t("igAccountAttention.awaiting2fa", {
            count: awaiting2fa.length,
            usernames,
          })}
        </span>
        <button
          type="button"
          className="ml-3 underline-offset-2 hover:underline"
          onClick={() => navigate("/instagram-accounts")}
        >
          {t("igAccountAttention.enterCode")}
        </button>
      </Banner>
    );
  }
  if (auth_required_in_error.length > 0) {
    const usernames = auth_required_in_error.map((a) => `@${a.igUsername}`).join(", ");
    return (
      <Banner tone="amber">
        <span>
          {t("igAccountAttention.authError", {
            count: auth_required_in_error.length,
            usernames,
          })}
        </span>
        <button
          type="button"
          className="ml-3 underline-offset-2 hover:underline"
          onClick={() => navigate("/instagram-accounts")}
        >
          {t("igAccountAttention.openAccounts")}
        </button>
      </Banner>
    );
  }
  return null;
}

function Banner({
  tone,
  children,
}: {
  tone: "rose" | "amber";
  children: React.ReactNode;
}): JSX.Element {
  const toneClass =
    tone === "rose"
      ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
      : "border-amber-500/40 bg-amber-500/10 text-amber-100";
  return (
    <div
      className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${toneClass}`}
      role="status"
    >
      {children}
    </div>
  );
}
